import "../assets/scss/pages/_product-page.scss";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons/faArrowLeft";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { addDays, format } from "date-fns";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams, Link, useLoaderData } from "react-router";

import { ActionButtons } from "../components/ActionButtons";
import { AddonPopup } from "../components/AddonPopup";
import { AddonSection } from "../components/AddonSection";
import { AddToCartButton } from "../components/AddToCartButton";
import { AvailableFrom } from "../components/AvailableFrom";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { DeliverySection } from "../components/DeliverySection";
import { FullReviewSection } from "../components/FullReviewSection";
import { HotelProductInformation } from "../components/HotelProductInformation";
import { KlarnaCTA } from "../components/KlarnaCTA";
import { LoadingAnimation } from "../components/LoadingAnimation";
import { MessageCardSectionNew } from "../components/MessageCardSectionNew";
import { MiniCartItem } from "../components/MiniCartItem";
import { OrderIn } from "../components/OrderIn";
import { OrderTotals } from "../components/OrderTotals";
import { PageSeo } from "../components/PageSeo";
import { PaymentOptions } from "../components/PaymentOptions";
import { Placeholder } from "../components/Placeholder";
import { ProductChoices } from "../components/ProductChoices";
import { ProductDescription } from "../components/ProductDescription";
import { ProductImage } from "../components/ProductImage";
import { ProductSizes } from "../components/ProductSizes";
import { RelatedProducts } from "../components/RelatedProducts";
import { Slider } from "../components/Slider";
import { StepTracker } from "../components/StepTracker";
import { useAppContext } from "../contexts/AppContext";
import { useViewportWidth } from "../hooks/useViewportWidth";
import { useAppStore } from "../stores/AppStore";
import { useCartStore } from "../stores/CartStore";
import { useHotelStore } from "../stores/HotelStore";
import { useProductStore } from "../stores/ProductStore";
import { useUserStore } from "../stores/UserStore";
import { BillingAddress } from "@components/BillingAddress";

const hasWindow = typeof window !== "undefined";
if (hasWindow) {
	window.dataLayer = window.dataLayer || [];
}

const GA4ProductData = (productInfo) => {
	if (!hasWindow) return;
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({
		event: "view_item",
		currency: "GBP",
		ecommerce: {
			detail: {
				products: {
					name: productInfo.products_name,
					id: productInfo.products_id,
					price: productInfo.price_retail,
					sku: productInfo.sku,
					category: productInfo.product_type,
					delivery_availability: productInfo.delivery?.lead_time,
					product_group: "base",
				},
			},
		},
	});

	if (!window.klaviyo) return;
	window.klaviyo?.track("Viewed Product", {
		ProductName: productInfo.products_name,
		ProductID: productInfo.products_id,
		SKU: productInfo.sku,
		ImageURL: productInfo.images && productInfo.images[0] ? productInfo.images[0].location : "",
		URL: productInfo.product_url,
		Brand: "123 Flowers",
		Price: productInfo.price_retail,
	});
};

const GA4AddToCart = (productInfo) => {
	if (!hasWindow) return;
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({
		event: "add_to_cart",
		currency: "GBP",
		ecommerce: {
			add: {
				products: {
					name: productInfo.products_name,
					id: productInfo.products_id,
					sku: productInfo.sku,
					quantity: productInfo.quantity || 1,
					price: productInfo.price_retail,
					category: productInfo.product_type,
					delivery_availability: productInfo.delivery.lead_time,
					product_group: "base",
				},
			},
		},
	});

	if (!window.klaviyo) return;
	window.klaviyo?.track("Added to Cart", {
		$value: productInfo.price_retail,
		AddedItemProductName: productInfo.products_name,
		AddedItemProductID: productInfo.products_id,
		AddedItemSKU: productInfo.sku,
		AddedItemImageURL: productInfo.images && productInfo.images[0] ? productInfo.images[0].location : "",
		AddedItemURL: productInfo.product_url,
		AddedItemPrice: productInfo.price_retail,
		AddedItemQuantity: 1,
		CheckoutURL: "https://www.123-flowers.co.uk/cart",
	});
};

const GA4BeginCheckout = (productInfo) => {
	if (!hasWindow) return;
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({
		event: "begin_checkout",
		currency: "GBP",
		ecommerce: {
			products: {
				name: productInfo.products_name,
				id: productInfo.products_id,
				price: productInfo.price_retail,
				sku: productInfo.sku,
				category: productInfo.product_type,
				delivery_availability: productInfo.delivery.lead_time,
				product_group: "base",
			},
		},
	});
};

export const ProductPage = ({ id = "no-id", initialInfo = null }) => {
	const params = useParams();
	const routeId = id === "no-id" ? params.id : id;
	const productId = Number(routeId) || 0;

	let dummyData = false;

	const [secondStage, setSecondStage] = useState(false);
	const [formFailed, setFormFailed] = useState(false);
	const [showFullAddress, setShowFullAddress] = useState(false);

	const [loaded, setLoaded] = useState(Boolean(initialInfo?.id));

	const [billingDetailsFormShown, setBillingDetailsFormShown] = useState(false);
	const [addToCartButtonsShown, setAddToCartButtonsShown] = useState(false);
	const [paymentFormShown, setPaymentFormShown] = useState(false);

	const { info: storeInfo, SetInfo, price, SetCurrentProductStep, AddToCart, address, productSteps, relatedProducts, selectedAddons, selectedDeliveryDate, selectedDeliveryOption, selectedSize, messageCardText, messageCardType, originalPrice, msrpPrice, SetAddedToCart, cartId, printzwareId, availableAddons, ResetProductStore } = useProductStore((state) => state);
	const info = initialInfo?.id ? initialInfo : storeInfo;

	const { cart, totals } = useCartStore(({ cart, totals }) => {
		return { cart, totals };
	});

	const { hotelFlow } = useHotelStore((state) => ({
		hotelFlow: state.hotelFlow,
	}));

	const { userInfo } = useUserStore(({ userInfo }) => {
		return { userInfo };
	});

	const billingInfo = userInfo?.billing_information ?? {};

	const isBillingIncomplete = !billingInfo.line_1 || !billingInfo.town || !billingInfo.postcode || !billingInfo.name;

	const loaderData = useLoaderData();
	const { peakConfigs: storedPeakConfigs } = useAppStore(({ peakConfigs }) => ({ peakConfigs }));
	const peakConfigs = loaderData?.peak_configs || storedPeakConfigs;

	const { isMobile } = useAppContext();

	const popupAddonId = "87"; //for some reason keys come back as strings

	const [showAddonPopup, setShowAddonPopup] = useState(false);
	const [popupCompleted, setPopupCompleted] = useState(null);

	const navigate = useNavigate();

	const billingDetailsCheck = () => {
		if(userInfo && Object.keys(userInfo).length > 0 && isBillingIncomplete) {
			setBillingDetailsFormShown(true);
		} else {
			setBillingDetailsFormShown(false);
		}
	};

	useEffect(() => {
		SetInfo(info);
		if (info.id && info.id !== 0) {
			setLoaded(true);
		}
		return () => {
			ResetProductStore();
		};
	}, [info]);

	useEffect(() => {
		try {
			document.querySelector(".main-content").scrollTo({ top: 0 });
		} catch (error) {
			console.error("Error scrolling to top of .main-content", error);
		}

		try {
			document.querySelector("html").scrollTo({ top: 0 });
		} catch (error) {
			console.error("Error scrolling to top of html", error);
		}
	}, [secondStage]);

	useEffect(() => {
		if (loaded === true && info) {
			try {
				GA4ProductData(info);
			} catch (error) {
				console.error("Error setting GA4 data", error);
			}
		}
	}, [loaded, info]);

	useEffect(() => {
		billingDetailsCheck();
	}, [userInfo]);

	if (!info) {
		return <LoadingAnimation pageType="Product Page" />;
	}

	let in_stock = true;
	in_stock = info.stock > 0 ? in_stock : false;
	in_stock = info && info.delivery && info.delivery.upcoming_dates && info.delivery.upcoming_dates.length > 0 ? in_stock : false;

	let images = { ...info.images };
	// delete images[0]; //commented out as we want the first image as well until reshoot

	if (useAppStore.getState().isDebug) {
		console.log("ProductPage.jsx - Product info", info);
	}

	images = dummyData
		? {
				0: {
					location: "https://images.prestigeflowers.co.uk/fetch/w_400,e_sharpen:80,q_auto,f_auto,dpr_auto,f_auto/https://www.prestigeflowers.co.uk/images/products/9c88cdaa0d6f23333564826e2ee15e8a.jpg",
				},
				1: {
					location: "https://images.prestigeflowers.co.uk/fetch/w_400,e_sharpen:80,q_auto,f_auto,dpr_auto,f_auto/https://www.prestigeflowers.co.uk/images/products/ed9ad31cb13a9cf9510ab115c05befce.jpg",
				},
				2: {
					location: "https://images.prestigeflowers.co.uk/fetch/w_400,e_sharpen:80,q_auto,f_auto,dpr_auto,f_auto/https://www.prestigeflowers.co.uk/images/products/b71ad05595ee0987863626f812e4699c.jpg",
				},
			}
		: images;

	const AddonPopupFunction = () => {
		setShowAddonPopup(true);

		return new Promise((resolve) => {
			setPopupCompleted(() => resolve);
		});
	};

	const AddToCartCheck = async (showCart = true) => {
		if (!address) return;

		const fields = ["address_name", "address_postcode", "address_line1", "address_town", "address_county"];

		const validChecks = fields.length;
		const validConfirm = fields.filter((field) => address[field] && address[field] !== "").length;

		if (validChecks === validConfirm) {
			setFormFailed(false);

			if (availableAddons && Object.keys(availableAddons).length !== 0 && Object.keys(availableAddons).includes(popupAddonId)) {
				//we have the addon available for purchase
				if (peakConfigs?.MOTHERS_DAY && !selectedAddons.includes(popupAddonId)) {
					await AddonPopupFunction();
				}
			}

			AddToCart({ showCart: showCart }).then((r) => {
				SetAddedToCart(r.length - 1);
				if (isMobile && showCart) {
					navigate("/cart");
				}
			});

			try {
				GA4AddToCart(info);
			} catch (error) {
				console.error("Error setting GA4 data", error);
			}

			return true;
		} else {
			setFormFailed(true);
			setShowFullAddress(true);
			document.querySelector(".delivery-form-section").scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
		}

		return false;
	};

	const [currentImageShowing, setCurrentImageShowing] = useState(0);

	const handleSlideChange = useCallback((slideInformation) => {
		if (slideInformation?.currentSlide !== undefined) {
			setCurrentImageShowing(slideInformation.currentSlide);
		}
	}, []);

	const sliderSlides = useMemo(() => {
		return Object.keys(images).map((image) => {
			return { image: images[image].location, imageWidth: 800 };
		});
	}, [images]);

	return (
		loaded === true && (
			<>
				<PageSeo
					title={info?.meta?.title?.trim() || info.products_name || ""}
					description={info?.full_description?.trim() || "A beautiful hand tied bouquet"}
					keywords={info?.meta?.keywords?.trim() || ""}
					canonical={info?.product_url?.trim() || ""}
					image={info?.images?.[0]?.location ?? ""}
					structuredData={{
						"@context": "http://schema.org",
						"@type": "Product",
						sku: info.sku,
						name: info.products_name,
						image: info.images?.[0]?.location || "",
						description: info.short_description || info.full_description || "A beautiful hand tied bouquet",
						brand: { "@type": "Brand", name: "123 Flowers" },
						offers: {
							"@type": "Offer",
							url: info.product_url,
							itemCondition: "https://schema.org/NewCondition",
							availability: (info.status === 'Enabled' && parseInt(info.stock) > 0) ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
							price: info.price_retail,
							priceCurrency: "GBP",
							priceValidUntil: format(addDays(new Date(), 30), "yyyy-MM-dd"),
							shippingDetails: {
								"@type": "OfferShippingDetails",
								shippingRate: { "@type": "MonetaryAmount", currency: "GBP", value: 0 },
								deliveryTime: {
									"@type": "ShippingDeliveryTime",
									businessDays: {
										"@type": "OpeningHoursSpecification",
										dayOfWeek: ["https://schema.org/Monday", "https://schema.org/Tuesday", "https://schema.org/Wednesday", "https://schema.org/Thursday", "https://schema.org/Friday", "https://schema.org/Saturday", "https://schema.org/Sunday"],
									},
									cutOffTime: `${info.delivery?.cutoff}:00:00Z`,
									handlingTime: {
										"@type": "QuantitativeValue",
										minValue: 1,
										maxValue: info.delivery?.lead_time,
										unitCode: "DAY",
									},
									transitTime: {
										"@type": "QuantitativeValue",
										minValue: 1,
										maxValue: 2,
										unitCode: "DAY",
									},
								},
								shippingDestination: { "@type": "DefinedRegion", addressCountry: "GB" },
							},
						},
					}}
				/>
				<div
					className={`product-page container ${secondStage ? "second-stage" : "first-stage"} ${formFailed ? "form-failed" : ""}`}
					id={info.id}
				>
					<div className="row align-items-stretch justify-content-md-space-between justify-content-center">
						<div className={`col-12 ${secondStage ? "col-md-4" : "col-md-6"} description-container`}>
							<>
								{Object.keys(images).length > 0 && (
									<section className="slider-section additional-images-section">
										<Slider
											slides={sliderSlides}
											prefix="additional-images"
											arrows={true}
											slides_per_row={secondStage ? { base: 1, md: 1 } : { base: 1, md: Object.keys(images).length === 1 ? 1 : 1.2 }}
											indicators={true}
											indicatorPreview={true}
											lightbox={true}
											onSlideChange={handleSlideChange}
										/>
									</section>
								)}
								{secondStage && (
									<section className="second-stage-sidebar-content">
										{hotelFlow ? (
											<div className="product-totals no-border">
												<h3>
													Total price: <span>£{parseFloat(price).toFixed(2)}</span>
												</h3>
											</div>
										) : (
											<>
												<ProductChoices
													setSecondStage={setSecondStage}
													SetCurrentProductStep={SetCurrentProductStep}
												/>
												<div className="product-totals">
													<h3>
														Total price: <span>£{parseFloat(price).toFixed(2)}</span>
													</h3>
												</div>
											</>
										)}
									</section>
								)}
							</>
						</div>

						<div className={`col-12 ${secondStage ? "col-md-8" : "col-md-6"} row column justify-content-start details-sidebar`}>
							<div className="details-container row">
								{!secondStage && <Breadcrumbs productName={info.products_name ?? ""} />}
								<div className="product-details col-12">
									{!secondStage ? (
										<>
											<div className="row align-items-center">
												<div className="col-fluid">
													<h1>{info.products_name ?? " "} </h1>
												</div>

												{in_stock && (
													<div className="col mob-only mobile-pricing row justify-content-lg-space-between align-items-end">
														{parseFloat(info.price_msrp) > parseFloat(info.price_retail) && <div className="msrp">£{parseFloat(msrpPrice).toFixed(2)}</div>}
														<div className="retail">£{parseFloat(originalPrice).toFixed(2)}</div>
													</div>
												)}

												{!secondStage && (
													<div className="col">
														<ActionButtons
															pos="right"
															product_id={info.id ?? 0}
															rating={info.review_average ?? 0}
														/>
													</div>
												)}
											</div>

											{info.quote && info.quote !== "" && <p className="quote">{info.quote}</p>}

											{info.has_sizes === "Yes" && in_stock && info.sizes.length && (
												<ProductSizes
													propSizes={info.sizes}
													currentImageShowing={currentImageShowing}
												/>
											)}

											{info?.id !== 2732 ? (
												<AddToCartButton
													inStock={in_stock}
													info={info}
													setSecondStage={setSecondStage}
													secondStage={secondStage}
												/>
											) : (
												<AddToCartButton
													bogus={true}
													info={info}
												/>
											)}

											{!hotelFlow && info?.id !== 2732 && (
												<>
													<strong className="delivery-from">
														<AvailableFrom date={info.stock > 0 && info.first_available_delivery !== "" ? info.first_available_delivery : false} />
													</strong>
													<OrderIn
														cutoff={info?.delivery_cutoff}
														product_id={info.id}
														first_available_delivery={info.first_available_delivery}
													/>
												</>
											)}

											<ProductDescription
												shortDescription={info.short_description}
												fullDescription={info.full_description}
												id={info.id}
												sku={info.sku}
												productType={info.product_type}
											/>

											<FullReviewSection slides_per_row={{ base: 1.1, md: 1.2 }} />

											<RelatedProducts info={relatedProducts} />
										</>
									) : (
										<>
											{isMobile && 1 == 2 && (
												<button
													className="btn back-to-product-information"
													onClick={() => {
														setSecondStage(false);
													}}
												>
													<FontAwesomeIcon icon={faArrowLeft} /> Back To Product Information
												</button>
											)}
											{info.has_sizes === "Yes" && in_stock && isMobile && (
												<ProductSizes
													propSizes={info.sizes}
													currentImageShowing={currentImageShowing}
												/>
											)}

											{hotelFlow ? (
												<HotelProductInformation />
											) : (
												<DeliverySection
													showFullAddress={showFullAddress}
													setShowFullAddress={setShowFullAddress}
												/>
											)}

											<MessageCardSectionNew />

											{!hotelFlow && <AddonSection />}

											<section className="add-to-cart-section">
												<div className="recap">
													<strong className="section-title">Your final product configuration:</strong>
													<p>For more details please check the sidebar featured to the left.</p>
												</div>

												{formFailed && <p className="form-failed">Please fill in all required fields to add the product to the basket.</p>}

												{((messageCardType === "standard" && messageCardText === "") || messageCardType === "") && <div className="alert error">You are sending your gift without a message card, please add one if this is a mistake or continue.</div>}

												<MiniCartItem
													image={info?.images?.[0]?.location.replace(".local", ".co.uk")}
													productName={info.products_name}
													price={price}
													size={selectedSize}
													deliveryDate={selectedDeliveryDate}
													deliveryAddress={address.address_name && address.address_line1 && address.address_town && address.address_postcode ? `${address.address_name} at ${address.address_line1}, ${address.address_town}, ${address.address_postcode}` : ""}
													deliveryType={selectedDeliveryOption ? selectedDeliveryOption.name.split("-")[0] : ""}
													addons={selectedAddons}
													removeButtonShown={false}
													messageCard={messageCardText}
													personalisedCardID={printzwareId}
													editButtonShown={false}
												/>

												{Object.keys(cart).length === 0 && !paymentFormShown && !billingDetailsFormShown &&  (
													<button
														className="buy-now-button emphasis-button"
														onClick={async () => {
															let res = await AddToCartCheck(false);
															if (res === true) {
																setPaymentFormShown(true);
																try {
																	GA4BeginCheckout(info);
																} catch (error) {
																	console.error("Error setting GA4 data", error);
																}
															}
														}}
													>
														Buy Now
													</button>
												)}

												{billingDetailsFormShown === true && (
													<div className="billing-details-container">
														<BillingAddress callback={() => { setBillingDetailsFormShown(false);}} />
													</div>
												)}

												<div className="payment-form">
													{paymentFormShown === "loading" && <LoadingAnimation />}

													{paymentFormShown === true && (
														<div className="payment-options-container">
															<OrderTotals
																totals={totals}
																cart={cart}
															/>
															<PaymentOptions
																cart={cart}
																userInfo={userInfo}
																totals={totals}
															/>
														</div>
													)}
												</div>

												{paymentFormShown === true && (
													<div className="or">
														<hr />
														OR
														<hr />
													</div>
												)}

												{billingDetailsFormShown === false ? (
													cartId === false ? (
														<button
															onClick={async () => {
																await AddToCartCheck();
															}}
															className={`add-to-basket ${paymentFormShown ? "faded" : ""} ${Object.keys(cart).length > 0 ? "emphasis-button" : ""}`}
														>
															Add To Basket &amp; Continue Shopping
														</button>
													) : (
														<>
															{Object.keys(cart).length !== 0 && !paymentFormShown && <div className="alert">Your product has been added to the basket now, please double check the information presented above and make any ammendments needed.</div>}

															<Link
																to="/cart"
																className="btn"
															>
																View Your Cart
															</Link>
														</>
													)
												) : null}

												<div className="klarna-cta">
													<KlarnaCTA
														cta="credit-promotion-auto-size"
														purchaseAmount={originalPrice}
													/>
												</div>
											</section>
										</>
									)}
								</div>

								{!hotelFlow && 1 == 2 && (
									<div className="row product-info justify-content-space-between step-container col-12">
										<div className="col-12 col-md-7 row column justify-content-end">
											<StepTracker
												productSteps={productSteps}
												setSecondStage={setSecondStage}
											/>
										</div>
										{!secondStage && (
											<div className="col-12 col-md-3 row column justify-content-end image-container">
												{dummyData && dummyData === true ? (
													<Placeholder check={null}>
														<img alt="Main Product" />
													</Placeholder>
												) : (
													<Placeholder check={null}>{info.images && <ProductImage image={info?.images[0]?.location} />}</Placeholder>
												)}
											</div>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
					{peakConfigs?.MOTHERS_DAY && showAddonPopup && availableAddons && Object.keys(availableAddons).length !== 0 && Object.keys(availableAddons).includes(popupAddonId) && (
						<AddonPopup
							addonId={popupAddonId}
							resolvePromise={popupCompleted}
							setShowAddonPopup={setShowAddonPopup}
						/>
					)}
				</div>
			</>
		)
	);
};

export default ProductPage;
